import { useEffect, useRef } from 'react';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { useDebugLogger } from '@/hooks/useDebugLogger';

type SSEConnectedTopic = {
  topic: string;
  serviceName: string;
  jobId: string;
};

type SSEConnectedMessage = {
  type: 'connected';
  topics: SSEConnectedTopic[];
  lastEventId: string | null;
  timestamp: string;
};

export type SSEMessageContext<TData = any> = {
  data: TData;
  eventName: string;
  rawEventName: string;
};

export type SSEOptions<TData = any> = {
  onMessage?: (context: SSEMessageContext<TData>) => void | Promise<void>;
  onUnhandledMessage?: (
    context: SSEMessageContext<TData>,
  ) => void | Promise<void>;
  /** 연결 성공(구독 시작) 직후 — 구독 전에 끝난 작업을 재조회할 때 */
  onOpen?: () => void;
  /** 연결 실패·서버 종료 — 재연결하지 않으므로 대기 중인 화면을 풀 때 */
  onDisconnect?: () => void;
};

export const useSSE = (
  topics: string[],
  handlers: { [key: string]: (data: any) => void },
  enabled = true,
  options?: SSEOptions,
) => {
  const abortControllerRef = useRef<AbortController | null>(null);
  const host = process.env.NEXT_PUBLIC_SSE_URL;
  const logger = useDebugLogger();

  // handlers 는 매 렌더 최신 값을 ref 에 보관한다.
  // 연결 useEffect 는 topics/enabled 로만 재실행되므로(재연결 방지),
  // onmessage 가 handlers 를 직접 클로저로 잡으면 마운트 시점 값에 고정된다.
  // 그 결과 나중에 로드되는 passportId(isMyEvent) 나 최신 onCompleted 가
  // 반영되지 않아 완료 이벤트가 무시된다(모달 열린 채 '정리중' 잔류).
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    if (!enabled) return;
    if (!host) {
      logger.error('[SSE-Core] NEXT_PUBLIC_SSE_URL is missing.');
      return;
    }

    const token = process.env.NEXT_PUBLIC_SSE_TOKEN;
    const cleanHost = host.replace(/\/$/, ''); // 끝 슬래시 제거

    const params = new URLSearchParams();
    topics.forEach((topic) => params.append('topics', topic));
    const url = `${cleanHost}/api/events/subscribe${
      topics ? `?${params.toString()}` : ''
    }`;

    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();

    const connectSSE = async () => {
      try {
        logger.log(`[SSE-Core] 📡 연결 시도: ${url}`);
        await fetchEventSource(url, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'text/event-stream',
          },
          signal: abortControllerRef.current?.signal,
          async onopen(response) {
            if (response.ok) {
              logger.log('[SSE-Core] ✅ 연결 성공');
              optionsRef.current?.onOpen?.();
            } else {
              logger.error(`[SSE-Core] ❌ 서버 응답 에러: ${response.status}`);
              // 여기서 에러를 던져야 onerror로 이동함
              throw new Error(`Server returned ${response.status}`);
            }
          },
          async onmessage(msg) {
            if (!msg.data || msg.data.trim() === '') return;

            try {
              const parsedData = JSON.parse(msg.data);
              const eventName = msg.event || 'message';

              logger.log('[SSE]', parsedData);

              // 1. 서버가 보내는 첫 연결 메시지('connected')는 그냥 통과시킵니다.
              if (parsedData.type === 'connected') {
                const connected = parsedData as SSEConnectedMessage;
                logger.log(
                  '[SSE-Core] 🔗 서버 구독 확인 완료:',
                  connected.topics,
                );
                return;
              }

              // 2. 실제 비즈니스 로직 이벤트 결정
              // 데이터 안에 type이 있으면 그걸 쓰고, 없으면 msg.event를 씁니다.
              const targetEvent =
                parsedData.type || parsedData.payload?.event || eventName;
              const context = {
                data: parsedData,
                eventName: targetEvent,
                rawEventName: eventName,
              };

              await options?.onMessage?.(context);

              const currentHandlers = handlersRef.current;
              if (targetEvent in currentHandlers) {
                await currentHandlers[targetEvent](parsedData);
              } else {
                await options?.onUnhandledMessage?.(context);
                logger.warn(
                  `[SSE-Core] ⚠️ 처리 핸들러 없음 (Event: ${targetEvent}):`,
                  parsedData,
                );
              }
            } catch (e) {
              logger.error('[SSE-Core] ❌ 핸들러 실행 실패:', e, msg.data);
            }
          },
          onclose() {
            logger.warn('[SSE-Core] ⚠️ 연결이 서버에 의해 닫혔습니다.');
            optionsRef.current?.onDisconnect?.();
          },
          onerror(err) {
            logger.error('[SSE-Core] 🧨 치명적 에러 발생:', err);
            // Failed to fetch가 여기서 잡히면 100% 네트워크/CORS 문제입니다.
            throw err; // 재연결 중단
          },
          openWhenHidden: true,
        });
      } catch (err: any) {
        if (err.name === 'AbortError') {
          logger.log('[SSE-Core] 🛑 연결 중단(Abort)');
        } else {
          logger.error('[SSE-Core] 🚨 연결 실패 (Failed to fetch):', err);
          optionsRef.current?.onDisconnect?.();
        }
      }
    };

    connectSSE();

    return () => abortControllerRef.current?.abort();
  }, [topics.join(','), enabled]);
};
